import Column from '@amzn/meridian/column';
import styled from '@emotion/styled';
import i18next from 'i18next';
import React, { FC } from 'react';
import { Text, TranslatedString, TranslationFunction } from '../../../components/blocks/localization';
import { formatMaxOrderableQuantity } from '../../../constants/orderQuantity';
import { PoReviewRow, ResellerItem } from '../../../redux/types';
import {
    findingKey,
    findingMessage,
    hasLiveForcedQuantityConcern,
    resolutionSentence,
    rowFindingsToRender,
} from '../poReviewFindings';

// The findings for one line, rendered as its own row beneath the line item.

export type PoReviewRowDetailProps = {
    t: TranslationFunction;
    row: PoReviewRow;
    itemMap: Map<string, ResellerItem>;
};

// The bullets sit in the indent so the sentences line up under the item column.
const FindingsList = styled.ul`
    margin: 0;
    padding-left: 24px;

    li + li {
        margin-top: 4px;
    }
`;

function quantitySentence(t: TranslationFunction, row: PoReviewRow): TranslatedString | undefined {
    if (!hasLiveForcedQuantityConcern(row)) {
        return undefined;
    }
    return t('quantityUnusable-text', {
        value: row.quantityRawValue ?? '',
        max: formatMaxOrderableQuantity(i18next.language),
    });
}

const PoReviewRowDetail: FC<PoReviewRowDetailProps> = ({ t, row, itemMap }) => {
    const resolutionText = resolutionSentence(t, row, itemMap);
    const quantityText = quantitySentence(t, row);
    return (
        <Column spacing={'xsmall'} spacingInset={'none none none xlarge'}>
            <FindingsList>
                {rowFindingsToRender(row).map((finding) => (
                    <li key={findingKey(finding, `${row.rowId}.finding`)}>
                        <Text type={'b200'}>{findingMessage(t, finding)}</Text>
                    </li>
                ))}
                {resolutionText && (
                    <li>
                        <Text type={'b200'}>{resolutionText}</Text>
                    </li>
                )}
                {quantityText && (
                    <li>
                        <Text type={'b200'}>{quantityText}</Text>
                    </li>
                )}
            </FindingsList>
        </Column>
    );
};

export { PoReviewRowDetail };
